import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";

import { UserAvatar } from "@/components/common/UserAvatar";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { DataTable } from "@/components/ui/data-table";
import { cn } from "@/lib/utils";
import {
  getTikTokBestsellingCreators,
  type TikTokBestsellingCreator,
  type TikTokCreatorTimeSlot,
} from "@/services/tiktokShop.service";
import { formatCompactCount, formatNumber } from "@/utils/formatters";

import { TikTokVideoThumb } from "./tiktok-video-thumb";

const SLOTS: Array<{ value: TikTokCreatorTimeSlot; label: string }> = [
  { value: "7D", label: "7 days" },
  { value: "30D", label: "30 days" },
];

const columns: ColumnDef<TikTokBestsellingCreator>[] = [
  {
    accessorKey: "rank",
    header: "#",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums text-muted-foreground">{row.original.rank}</span>,
  },
  {
    accessorKey: "username",
    header: "Creator",
    cell: ({ row }) => (
      <span className="flex min-w-0 items-center gap-2.5">
        {/* Synced from TikTok's Creator Marketplace; initials until then. */}
        <UserAvatar
          src={row.original.avatarUrl}
          name={row.original.nickname || row.original.username}
          className="h-8 w-8 shrink-0 text-xs"
        />
        <span className="flex min-w-0 flex-col">
          <a
            href={row.original.profileUrl ?? `https://www.tiktok.com/@${row.original.username}`}
            target="_blank"
            rel="noreferrer"
            className="max-w-[11rem] truncate font-medium text-foreground hover:underline"
            title={row.original.nickname}
          >
            {row.original.nickname || row.original.username}
          </a>
          <span className="max-w-[11rem] truncate text-xs text-muted-foreground">@{row.original.username}</span>
        </span>
      </span>
    ),
  },
  {
    id: "topVideos",
    header: "Top videos",
    meta: { fit: true },
    cell: ({ row }) =>
      row.original.topVideos.length > 0 ? (
        <span className="flex items-center gap-1">
          {row.original.topVideos.map((video) => (
            <a
              key={video.id}
              href={video.url}
              target="_blank"
              rel="noreferrer"
              aria-label={video.title || "Open video on TikTok"}
              title={video.title ? `${video.title} · ${formatCompactCount(video.views)} views` : undefined}
              className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <TikTokVideoThumb src={video.thumbnailUrl} title={video.title} className="h-10 w-[1.5rem]" />
            </a>
          ))}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: "gmvRange",
    header: "GMV",
    meta: { fit: true },
    cell: ({ row }) => (
      // TikTok only discloses a range per creator, never an exact figure.
      <span className="whitespace-nowrap tabular-nums font-medium text-foreground">{row.original.gmvRange}</span>
    ),
  },
  {
    accessorKey: "followersCount",
    header: "Followers",
    meta: { fit: true },
    cell: ({ row }) => <span className="tabular-nums">{formatNumber(row.original.followersCount)}</span>,
  },
  {
    accessorKey: "affiliate",
    header: "In program",
    meta: { fit: true },
    cell: ({ row }) =>
      row.original.affiliate ? (
        <span
          className="rounded-md border border-border px-1.5 py-0.5 text-[11px] font-medium text-foreground"
          title={row.original.affiliate.name}
        >
          Affiliate
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
  },
];

/**
 * TikTok's bestselling creators for the connected shop, ranked by GMV, with
 * the ones who are already the brand's affiliates tagged. The list is the
 * recruiting radar: a top seller without the tag is a creator worth inviting.
 */
export function TikTokCreatorsCard() {
  const [slot, setSlot] = useState<TikTokCreatorTimeSlot>("7D");
  const query = useQuery({
    queryKey: ["tiktok-bestselling-creators", slot],
    queryFn: () => getTikTokBestsellingCreators(slot),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return (
    <DashboardCard
      id="dashboard-tiktok-creators-card"
      title="Top TikTok creators"
      action={
        <div role="group" aria-label="Period" className="inline-flex items-center rounded-md bg-muted p-0.5">
          {SLOTS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={slot === option.value}
              disabled={query.isFetching}
              onClick={() => setSlot(option.value)}
              className={cn(
                "rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                slot === option.value
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      }
      bodyClassName="px-3 pb-3"
    >
      {query.isError ? (
        <p className="px-2 pb-2 text-sm text-muted-foreground">
          TikTok creator rankings could not be loaded. Try again in a few minutes.
        </p>
      ) : (
        <div className="min-h-[14.5rem]">
          <DataTable
            columns={columns}
            data={(query.data?.creators ?? []).slice(0, 10)}
            isLoading={query.isLoading}
            skeletonRows={5}
            getRowId={(row) => row.openId || String(row.rank)}
            emptyText="No creator sales on TikTok Shop in this period"
            minTableWidth="min-w-0"
          />
          {query.data?.date && (
            <p className="px-2 pt-2 text-xs text-muted-foreground">
              Ranked by TikTok for the {slot === "7D" ? "7 days" : "30 days"} ending {query.data.date}.
            </p>
          )}
        </div>
      )}
    </DashboardCard>
  );
}
